import type { Messages } from './en.js'

/** Brazilian Portuguese. Typed against `Messages`, so a missing key will not compile. */
export const ptBR: Messages = {
  'common.save': 'Salvar',
  'common.cancel': 'Cancelar',
  'common.loading': 'Carregando…',
  'common.retry': 'Tentar novamente',
  'common.copy': 'Copiar',
  'common.copied': 'Copiado',
  'common.remove': 'Remover',
  'common.rename': 'Renomear',
  'common.expand': 'Abrir',
  'common.collapse': 'Fechar',
  'common.none': 'Nenhum',

  'nav.trips': 'Viagens',
  'nav.signOut': 'Sair',
  'nav.settings': 'Ajustes',
  'nav.language': 'Idioma',
  'nav.account': 'Sua conta',

  'account.title': 'Sua conta',
  'account.signedInAs': 'Você está conectada como {email}.',
  'account.password.title': 'Alterar sua senha',
  'account.password.current': 'Senha atual',
  'account.password.new': 'Nova senha',
  'account.password.confirm': 'Digite a nova senha novamente',
  'account.password.submit': 'Alterar senha',
  'account.password.lengthHint': 'No mínimo {min} caracteres.',
  'account.password.changed':
    'Sua senha foi alterada. Todos os outros aparelhos em que você estava conectada foram desconectados.',
  'account.password.mismatch': 'As duas não são iguais.',
  'account.password.tooShort': 'Curta demais — no mínimo {min} caracteres.',
  'account.password.sameAsCurrent': 'Essa já é a sua senha.',
  'account.password.wrongCurrent': 'Essa não é a sua senha atual.',
  'account.password.current.guidance':
    'Pedimos mesmo com você já conectada. É o que impede que alguém com acesso a este navegador tranque você para fora da sua própria conta.',
  'account.password.new.guidance':
    'O comprimento é o que torna uma senha difícil de adivinhar — uma frase com quatro ou cinco palavras comuns vale mais do que uma senha curta cheia de símbolos. Alterá-la aqui desconecta todos os outros celulares e computadores em que você estava conectada.',

  'login.title': 'Entrar',
  'login.email': 'E-mail',
  'login.password': 'Senha',
  'login.submit': 'Entrar',
  'login.failed': 'E-mail e senha não conferem.',

  // Orientação de campo. Tudo abaixo com `*.guidance` é andaime — veja a nota
  // sobre `guidanceLevel` em contracts/auth.ts. Aposentar é apagar todas as
  // chaves com `.guidance` deste arquivo e do en.ts, e nada mais.
  'guidance.label': 'Explicações dos campos',
  'guidance.full': 'Explicar campos',
  'guidance.brief': 'Dicas curtas',
  'guidance.off': 'Sem dicas',

  'timezone.searchPlaceholder': 'Busque uma cidade — Paris, Londres, São Paulo…',
  'timezone.suggestionsLabel': 'Destinos frequentes, ou busque uma cidade acima.',
  'timezone.noMatches': 'Nenhuma cidade com esse nome. Tente a cidade grande mais próxima.',
  'timezone.listLabel': 'Cidades',
  'timezone.confirm': 'Agora são {time} em {city}. Se isso parecer errado, a cidade está errada.',
  'timezone.sameAsTrip': 'A mesma da viagem — {city}',
  'timezone.elsewhere': 'Outro lugar',
  'timezone.landsSame': 'No mesmo lugar — {city}',
  'timezone.landsElsewhere': 'Termina em outro lugar',
  'timezone.flightReadsAs': 'Sai {depTime} em {depCity}, chega {arrTime} em {arrCity}.',
  'timezone.duration': 'Cerca de {hours}h de voo.',
  'timezone.readsAs': '{time} em {city}. A página do viajante vai mostrar {tripTime}, porque ela lê os horários no fuso de {tripCity}.',

  'trips.title': 'Viagens',
  'trips.empty': 'Nenhuma viagem ainda.',
  'trips.new': 'Nova viagem',
  'trips.field.title': 'Nome da viagem',
  'trips.field.title.guidance': 'Como você vai reconhecer esta viagem na lista. O viajante vê isto como o título da página dele, então escreva do jeito que ele escreveria: “Família Longhi — Paris, julho”, não “Job 214”.',
  'trips.field.startDate': 'Data de início',
  'trips.field.startDate.guidance': 'O primeiro dia da viagem. Um dia é criado para cada data entre esta e a de término, prontos para você preencher. Mudar depois acrescenta os dias que faltam e nunca apaga um dia em que você já escreveu.',
  'trips.field.endDate': 'Data de término',
  'trips.field.endDate.guidance': 'O último dia da viagem. A validade do passaporte é conferida contra esta data — inclusive a regra dos seis meses após o retorno, exigida por muitos países — então vale acertá-la antes de cadastrar documentos.',
  'trips.field.timezone': 'Onde a viagem acontece',
  'trips.field.timezone.guidance': 'A cidade cujo relógio o viajante vai seguir. Todo horário de reserva que você digitar aparece para ele neste relógio, a não ser que você defina outra cidade naquela reserva — que é o que se faz num voo que cruza fusos.',
  'trips.field.status': 'Situação',
  'trips.status.planning': 'Planejamento',
  'trips.status.ticketed': 'Emitida',
  'trips.status.travelling': 'Em viagem',
  'trips.status.complete': 'Concluída',
  'trips.dates': 'de {start} a {end}',

  'participants.title': 'Viajantes',
  'participants.empty': 'Ninguém adicionado ainda.',
  'participants.add': 'Adicionar viajante',
  'participants.field.displayName': 'Nome',
  'participants.field.displayName.guidance': 'Como esta pessoa é cumprimentada ao abrir o link dela. Ela não pode alterar, então escreva como ela escreveria — em geral só o primeiro nome.',
  'participants.field.role': 'Função',
  'participants.field.role.guidance': 'Uma consultora vê tudo da viagem. Um viajante vê o roteiro e os documentos dele, nunca os de outro viajante.',
  'participants.field.email': 'E-mail',
  'participants.field.email.guidance': 'Guardado para você entrar em contato. Não é login — ninguém cria conta aqui — e nunca aparece para os outros viajantes.',
  'participants.field.phone': 'Telefone',
  'participants.field.phone.guidance': 'O número para o qual você ligaria se um voo mudasse. Fica guardado para você; não aparece na página do viajante.',
  'participants.role.advisor': 'Consultora',
  'participants.role.traveller': 'Viajante',
  'participants.nameNote': 'Você define este nome. Quem abre o link não pode alterá-lo.',
  'participants.removeConfirm': 'Remover {name} desta viagem?',

  'share.title': 'Links',
  'share.empty': 'Nenhum link ainda.',
  'share.create': 'Criar link',
  'share.createFor': 'Criar link para {name}',
  'share.unbound': 'Sem nome',
  'share.boundTo': 'Para {name}',
  'share.revoke': 'Revogar',
  'share.revoked': 'Revogado',
  'share.revokeConfirm': 'Revogar este link? Quem o tiver perde o acesso imediatamente.',

  'share.nameIt': 'Dar um nome',
  'share.namePlaceholder': 'grupo do WhatsApp, cópia do Carlos…',
  'share.nameHint': 'Só você vê. É como você distingue um link do outro.',
  'share.allRevoked': 'Todos os links foram revogados. Crie um novo para compartilhar esta viagem.',
  'share.showRevoked': 'Mostrar {count} revogado(s)',
  'share.hideRevoked': 'Ocultar revogados',

  'sharePage.greeting': 'Olá, {name}',
  'sharePage.anonymous': 'Sua viagem',
  'sharePage.preparedBy': 'Preparado por {agency}',
  'sharePage.colophon': 'Roteiro preparado por {agency} ·',
  'sharePage.print': 'Imprimir / Salvar PDF',
  'sharePage.notFound': 'Este link não é válido. Peça um novo à sua consultora.',

  'plan.title': 'Roteiro',
  'progress.dayOf': 'Dia {day} de {total}',
  'progress.startsToday': 'Começa hoje',
  'progress.startsIn': 'Em {days} dias',
  'progress.underway': 'Em andamento',
  'progress.next': 'A seguir:',
  'progress.nothingScheduled': 'Nada mais com horário marcado.',
  'progress.jumpToToday': 'Ir para hoje',

  // O que mudou desde a última visita. Duas chaves em vez de uma com `{count}`:
  // em português o verbo concorda com o número, e "1 coisas mudaram" estraga
  // justamente a frase que precisa soar como um recado da consultora.
  'updates.oneChanged': '1 coisa mudou desde a sua última visita',
  'updates.manyChanged': '{count} coisas mudaram desde a sua última visita',
  'updates.limits': 'A contagem é deste aparelho e não mostra o que foi tirado do roteiro.',
  'updates.show': 'Ver o que mudou',
  'updates.dismiss': 'Entendi',
  'updates.added': 'Novo',
  'updates.edited': 'Alterado',

  // A cópia offline. A regra é que ela nunca se passe por atualizada: quem está
  // num guichê com o roteiro na mão precisa saber de quando ele é. Só
  // `storedCopy` sai no papel — a folha impressa a partir de uma cópia guardada
  // merece carregar a data; o resto fala do sinal do celular naquele minuto.
  'offline.storedCopy': 'Esta é a cópia guardada neste aparelho, de {when}.',
  'offline.mayHaveChanged': 'Você está sem conexão, então o que mudou depois disso ainda não aparece aqui. Abra de novo quando tiver sinal.',
  'offline.notStored': 'Você está sem conexão, e este roteiro ainda não foi guardado neste aparelho. Abra o link uma vez com internet e depois ele abre sem ela.',
  'offline.needsConnection': 'Isto precisa de conexão. Tente de novo quando tiver sinal.',

  'plan.emergency': 'Emergência',
  'plan.unscheduled': 'Ainda sem data',
  'plan.empty': 'Nada aqui ainda.',
  'plan.confirmation': 'Confirmação',
  'plan.openMap': 'Mapa',
  'plan.call': 'Ligar',
  'plan.website': 'Site',
  'plan.localTimeIn': 'horário local de {place}',
  'plan.noDay': 'Sem dia',

  'reservation.kind.flight': 'Voo',
  'reservation.kind.train': 'Trem',
  'reservation.kind.hotel': 'Hotel',
  'reservation.kind.car': 'Carro',
  'reservation.kind.transfer': 'Transfer',
  'reservation.kind.restaurant': 'Restaurante',
  'reservation.kind.activity': 'Passeio',
  'reservation.kind.other': 'Outro',

  'day.notes': 'Notas deste dia',
  'day.notesHint': 'Markdown. Links e telefones ficam clicáveis.',
  'day.notes.guidance': 'O que o viajante precisa ler sobre este dia, nas suas palavras. Aparece acima das reservas do dia na página dele e na versão impressa.',

  'places.title': 'Lugares',
  'places.add': 'Adicionar lugar',
  'places.field.name': 'Nome',
  'places.field.name.guidance': 'Como o lugar se chama. Aparece para o viajante e vira o rótulo do link do mapa.',
  'places.field.address': 'Endereço',
  'places.field.address.guidance': 'O endereço como você escreveria. Usado no link do mapa quando não há coordenadas.',
  'places.field.addressLocal': 'Endereço no idioma local',
  'places.field.addressLocalHint': 'O que você mostraria ao motorista.',
  'places.field.addressLocal.guidance': 'O mesmo endereço no idioma e no alfabeto locais. Aparece em linha própria para poder ser mostrado a um motorista que não lê português — na página impressa, é a linha que mais resolve.',
  'places.field.lat': 'Latitude',
  'places.field.lat.guidance': 'Opcional. Com coordenadas, o link do mapa leva à porta e não à rua. Preencha junto com a longitude — sozinha, é ignorada.',
  'places.field.lng': 'Longitude',
  'places.field.lng.guidance': 'A outra metade das coordenadas. Cole as duas do mapa, ou deixe as duas vazias e use só o endereço.',
  'places.field.website': 'Site',
  'places.field.website.guidance': 'Abre em nova aba a partir da página do viajante. Só endereços http e https aparecem — qualquer outro é descartado em vez de exibido.',
  'places.field.phone': 'Telefone',
  'places.field.phone.guidance': 'Vira um link de ligar no celular do viajante. Inclua o código do país, senão não completa a chamada do exterior.',
  'places.field.notes': 'Notas',
  'places.field.day': 'Dia',

  'reservations.title': 'Reservas',
  'reservations.add': 'Adicionar reserva',
  'reservations.field.kind': 'Tipo',
  'reservations.field.kind.guidance': 'Define o rótulo que o viajante vê nesta reserva. Não muda mais nada — todos os tipos têm os mesmos campos.',
  'reservations.field.title': 'Descrição',
  'reservations.field.title.guidance': 'O que é esta reserva, em uma linha: “LATAM 8064 GRU → CDG” ou “Hotel du Louvre, 3 noites”. É o título que o viajante lê.',
  'reservations.field.confirmationRef': 'Número de confirmação',
  'reservations.field.confirmationRef.guidance': 'O número que alguém lê em voz alta num balcão. Aparece como texto puro e nunca vira link, para que um número de apólice não seja confundido com telefone.',
  'reservations.field.startsAt': 'Início',
  'reservations.field.startsAt.guidance': 'Quando começa, no relógio da cidade indicada abaixo. Um voo que sai de São Paulo começa às 14:40 em São Paulo — não às 14:40 em Paris.',
  'reservations.field.endsAt': 'Fim',
  'reservations.field.timezone': 'Os horários acima são no relógio de',
  'reservations.field.timezone.guidance': 'De qual cidade é o relógio que você está lendo. Quase tudo acontece onde a viagem está, então a cidade da viagem é a resposta de sempre. O voo que cruza fusos é a exceção, e é aqui que você diz isso — aí o viajante vê a partida marcada com a cidade de onde ela sai.',

  'trips.field.emergencyInfo': 'Informações de emergência',
  'trips.field.emergencyInfoHint': 'Consulado, seguro, seu celular. Aparece no topo da página do viajante.',
  'trips.field.emergencyInfo.guidance': 'Fica acima do roteiro na página do viajante e no alto da versão impressa, para ser achado por alguém que não está calmo.',

  'money.title': 'Prazos e valores',
  'money.totals.title': 'Total acumulado',
  'money.totals.empty': 'Nenhum custo ainda.',
  'money.deadlines.title': 'O que precisa de você, e até quando',
  'money.deadlines.empty': 'Nenhum prazo ainda.',
  'money.deadlines.add': 'Adicionar prazo',
  'money.deadlines.markDone': 'Marcar como feito',
  'money.deadlines.reopen': 'Reabrir',
  'money.deadlines.field.label': 'O quê',
  'money.deadlines.field.label.guidance': 'O que precisa acontecer — “pagar o saldo”, “enviar os dados do passaporte”. Os prazos são só seus; a página do viajante não os mostra.',
  'money.deadlines.field.dueAt': 'Até quando',
  'money.deadlines.field.dueAt.guidance': 'A data em que deixa de ser cedo e passa a ser tarde. O que passar da data é marcado como perdido neste cartão.',
  'money.deadlines.field.owner': 'De quem é a vez',
  'money.deadlines.field.owner.guidance': 'Se isso está esperando você ou o viajante. Nada acontece automaticamente — está aqui para você ver de relance qual lista é a sua.',
  'money.deadlines.owner.advisor': 'Consultora',
  'money.deadlines.owner.traveller': 'Viajante',
  'money.deadlines.status.open': 'Aberto',
  'money.deadlines.status.done': 'Feito',
  'money.deadlines.status.missed': 'Perdido',
  'money.costLines.title': 'Custos',
  'money.costLines.empty': 'Nenhum custo ainda.',
  'money.costLines.add': 'Adicionar custo',
  'money.costLines.field.label': 'O quê',
  'money.costLines.field.label.guidance': 'Para que é o dinheiro. Aparece no total acumulado deste cartão, que só você vê.',
  'money.costLines.field.amount': 'Valor',
  'money.costLines.field.amount.guidance': 'O valor na moeda ao lado. Os totais ficam separados por moeda e nunca são convertidos — somar euros a reais pela cotação de hoje daria um número já errado quando alguém for pagar.',
  'money.costLines.field.currency': 'Moeda',
  'money.costLines.field.currency.guidance': 'Três letras: BRL, EUR, USD. Cada moeda ganha a própria linha no total.',
  'money.costLines.field.dueAt': 'Vencimento',
  'money.costLines.status.quoted': 'Cotado',
  'money.costLines.status.committed': 'Confirmado',
  'money.costLines.status.paid': 'Pago',
  'money.removeConfirm': 'Remover {label}?',
  // Build unit 7.16 — dados de documentos de viagem. Campos, nunca imagens.
  'travelDocuments.title': 'Documentos de viagem',
  'travelDocuments.empty': 'Nenhum documento registrado.',
  'travelDocuments.add': 'Adicionar documento',
  'travelDocuments.edit': 'Editar',
  'travelDocuments.noImageNote': 'Ana digita estes dados a partir do documento — não há envio de foto aqui. Uma foto enviada pelo WhatsApp não precisa ser guardada.',
  'travelDocuments.field.kind': 'Tipo de documento',
  'travelDocuments.field.kind.guidance': 'O que você está registrando. Passaportes são conferidos contra as datas da viagem; identidades e vistos são guardados sem essa conferência.',
  'travelDocuments.kind.passport': 'Passaporte',
  'travelDocuments.kind.id_card': 'Carteira de identidade',
  'travelDocuments.kind.visa': 'Visto',
  'travelDocuments.field.fullNameAsPrinted': 'Nome como impresso',
  'travelDocuments.field.fullNameAsPrintedHint': 'Exatamente como aparece no documento — as companhias aéreas conferem letra por letra.',
  'travelDocuments.field.fullNameAsPrinted.guidance': 'Copie exatamente, com acentos e tudo. Se for diferente do nome que você deu a este viajante, você recebe um aviso aqui — melhor do que a companhia aérea notar a diferença no check-in.',
  'travelDocuments.field.documentNumber': 'Número do documento',
  'travelDocuments.field.documentNumber.guidance': 'O número impresso no documento. Você digita; aqui não há envio de foto, então a imagem que te mandaram pelo WhatsApp não precisa ser guardada em lugar nenhum.',
  'travelDocuments.field.nationality': 'Nacionalidade',
  'travelDocuments.field.nationality.guidance': 'A nacionalidade que consta no documento, que nem sempre é a do país que o emitiu. Duas letras.',
  'travelDocuments.field.issuingCountry': 'País emissor',
  'travelDocuments.field.issuingCountry.guidance': 'O país que emitiu o documento. Duas letras.',
  'travelDocuments.field.dateOfBirth': 'Data de nascimento',
  'travelDocuments.field.dateOfBirth.guidance': 'Como está impressa no documento, para os formulários de reserva que pedem.',
  'travelDocuments.field.expiresOn': 'Data de validade',
  'travelDocuments.field.expiresOn.guidance': 'Comparada com a data de término da viagem. Você é avisada duas vezes: se vencer durante a viagem, e se vencer dentro de seis meses após o retorno — muitos países barram a entrada só por isso.',
  'travelDocuments.field.purgeAfter': 'Mantido até',
  'travelDocuments.countryHint': 'Código de duas letras, ex.: BR ou US.',
  'travelDocuments.removeConfirm': 'Remover este documento? Os dados não poderão ser recuperados.',
  'travelDocuments.warning.expired_before_return': 'Vence em {expiresOn}, antes do retorno da viagem em {returnDate}.',
  'travelDocuments.warning.expires_within_six_months': 'Vence em {expiresOn} — muitos destinos exigem seis meses de validade após a data de retorno ({returnDate}).',
  'travelDocuments.warning.name_mismatch': 'Impresso como “{printedName}”, salvo aqui como “{displayName}”.',
  'consent.title': 'Saúde, alimentação e acessibilidade',
  'consent.hint': 'Dados sensíveis segundo a LGPD. Registrar um requer o consentimento do viajante ao texto exato abaixo — não uma caixinha marcada nos termos.',
  'consent.empty': 'Nada registrado.',
  'consent.purpose.health': 'Saúde',
  'consent.purpose.dietary': 'Alimentação',
  'consent.purpose.accessibility': 'Acessibilidade',
  'consent.add': 'Registrar algo',
  'consent.edit': 'Atualizar',
  'consent.field.purpose': 'Categoria',
  'consent.field.purpose.guidance': 'Define o texto de consentimento mostrado abaixo — que é exatamente o texto com que o viajante precisa concordar, então escolha a categoria que corresponde ao que você vai escrever.',
  'consent.field.detail': 'O que você está registrando?',
  'consent.field.detailHint': 'Exemplo: “Diabético tipo 1, usa insulina.”',
  'consent.field.detail.guidance': 'O que te contaram, no menor número de palavras que resolva. Isto é dado sensível pela LGPD: só salva com um consentimento registrado, e retirar esse consentimento torna a informação ilegível em todo o aplicativo na mesma hora.',
  'consent.preview': 'Este é o consentimento que você está registrando:',
  'consent.confirm': 'Expliquei isso ao viajante e obtive o consentimento dele(a) ao texto acima',
  'consent.record': 'Registrar consentimento e salvar',
  'consent.grantedOn': 'Consentido em {date}',
  'consent.withdraw': 'Retirar',
  'consent.withdrawConfirm': 'Retirar este consentimento? A informação registrada fica ilegível imediatamente, em todo o aplicativo.',
  'consent.withdrawn': 'Retirado em {date}',
  'consent.noteLabel': 'Nota registrada',
  'consent.noteUnreadable': 'Retirado — não pode mais ser lido. Registre um novo consentimento para ver ou alterar.',
  'consent.statement.health': 'Autorizo o registro de informação de saúde sobre {participant} pela {agency}, para fins de planejamento e apoio a esta viagem, e seu compartilhamento com quem precisar dela para manter {participant} em segurança durante a viagem.',
  'consent.statement.dietary': 'Autorizo o registro de informação alimentar sobre {participant} pela {agency}, para fins de planejamento e apoio a esta viagem, e seu compartilhamento com estabelecimentos e fornecedores conforme necessário.',
  'consent.statement.accessibility': 'Autorizo o registro de necessidades de acessibilidade de {participant} pela {agency}, para fins de planejamento e apoio a esta viagem, e seu compartilhamento com estabelecimentos e fornecedores conforme necessário.',
  'files.title': 'Documentos',
  'files.empty': 'Nenhum documento ainda.',
  'files.upload': 'Enviar documento',
  'files.uploading': 'Enviando…',
  'files.field.file': 'Arquivo',
  'files.field.file.guidance': 'Até 20 MB. Vouchers, passagens, certificados de seguro — não fotos de passaporte, que ficam nos campos de documento acima, digitados em vez de guardados.',
  'files.field.category': 'Categoria',
  'files.field.category.guidance': 'Como o arquivo é rotulado na página do viajante. Nada mais depende disso.',
  'files.field.sensitivity': 'Sensibilidade',
  'files.field.sensitivity.guidance': 'Um arquivo compartilhável baixa com um toque. Um sensível pede o código antes, e esse código nunca vai no link — então um link repassado não abre nada.',
  'files.field.unlockCode': 'Código de desbloqueio',
  'files.field.unlockCodeHint': 'Compartilhe com o viajante por telefone ou mensagem, nunca por e-mail.',
  'files.field.unlockCode.guidance': 'O código que o viajante digita para abrir este arquivo. Mande por telefone ou mensagem: por e-mail, o arquivo e a chave dele ficam na mesma caixa de entrada, o que equivale a não ter código.',
  'files.category.voucher': 'Voucher',
  'files.category.insurance': 'Certificado de seguro',
  'files.category.ticket': 'Passagem',
  'files.category.other': 'Outro',
  'files.sensitivity.sensitive': 'Sensível — precisa de código',
  'files.sensitivity.shareable': 'Compartilhável — sem código',
  'files.sensitiveBadge': 'Bloqueado',
  'files.download': 'Baixar',
  'files.downloading': 'Baixando…',
  'files.enterCode': 'Digitar código',
  'files.unlockPrompt': 'Digite o código de desbloqueio para baixar este arquivo.',
  'files.unlockSubmit': 'Desbloquear e baixar',
  'files.unlockFailed': 'Código incorreto, ou este arquivo não está mais disponível.',
  'files.removeConfirm': 'Remover {name}? Isso não pode ser desfeito.',
  'files.retention': 'Mantido até {date}',
  'files.tooLarge': 'Esse arquivo é maior que 20 MB.',
  'files.badType': 'Esse tipo de arquivo não é aceito.',

  'places.removeConfirm': 'Remover {name} desta viagem? O viajante deixa de ver na hora.',
  'reservations.removeConfirm': 'Remover {name}? O viajante deixa de ver na hora.',

  'savedPlaces.title': 'Meus lugares',
  'savedPlaces.hint': 'Lugares que você indica, guardados entre viagens. Adicionar um a uma viagem faz uma cópia, então editar aqui nunca muda um roteiro que alguém já tem.',
  'savedPlaces.empty': 'Nada guardado ainda. Adicione um lugar abaixo, ou toque em “Guardar” ao lado de qualquer lugar desta viagem.',
  'savedPlaces.add': 'Guardar um lugar',
  'savedPlaces.addToTrip': 'Adicionar à viagem',
  'savedPlaces.keep': 'Guardar',
  'savedPlaces.here': 'aqui',
  'savedPlaces.search': 'Buscar nos seus lugares',
  'savedPlaces.removeConfirm': 'Remover {name} dos seus lugares? As viagens que já usam não são afetadas.',
  'savedPlaces.addFrom': 'Dos meus lugares',
  'savedPlaces.noMatches': 'Nada encontrado. Tente menos palavras, ou limpe os filtros.',
  'savedPlaces.filter.destination': 'Destino',
  'savedPlaces.filter.allDestinations': 'Todos os destinos',
  'savedPlaces.filter.kind': 'Tipo',
  'savedPlaces.filter.allKinds': 'Todos os tipos',
  'savedPlaces.field.kind': 'Que tipo de lugar',
  'savedPlaces.field.kind.guidance': 'Filtra a lista quando você já tiver muitos. Propositalmente grosseiro — uma categoria detalhada demais é uma que ninguém preenche do mesmo jeito duas vezes, e “outro” é uma resposta de verdade.',
  'savedPlaces.kind.restaurant': 'Restaurante',
  'savedPlaces.kind.hotel': 'Hotel',
  'savedPlaces.kind.cafe': 'Café',
  'savedPlaces.kind.bar': 'Bar',
  'savedPlaces.kind.museum': 'Museu',
  'savedPlaces.kind.shop': 'Loja',
  'savedPlaces.kind.activity': 'Passeio',
  'savedPlaces.kind.other': 'Outro',
  'savedPlaces.field.destination': 'Onde fica',
  'savedPlaces.field.destination.guidance': 'Como você agrupa — “Paris”, “Toscana”, “Lisboa”. Os lugares que combinam com a cidade da viagem que você está montando aparecem primeiro; o resto fica a uma busca de distância, então uma resposta aproximada não custa nada.',
  'savedPlaces.field.notes.guidance': 'Por que você indica, nas suas palavras. É copiado para a viagem junto com o resto, então escreva como o viajante deve ler.',

  'snippets.title': 'Meus textos',
  'snippets.hint': 'Os parágrafos que você escreve em toda viagem. Inserir um faz uma cópia, então melhorar aqui nunca reescreve um roteiro que alguém já tem.',
  'snippets.empty': 'Nada guardado ainda. Comece pelo que você mais redigita — como sair do aeroporto, como funciona a gorjeta, o que fazer se sumir um passaporte.',
  'snippets.add': 'Guardar um texto',
  'snippets.addFrom': 'Dos meus textos',
  'snippets.insertHint': 'O texto é copiado para esta nota. O que já estiver escrito permanece, e você pode ajustar a cópia sem mexer no original.',
  'snippets.search': 'Buscar nos seus textos',
  'snippets.noMatches': 'Nada encontrado. Tente menos palavras, ou limpe os filtros.',
  'snippets.removeConfirm': 'Remover “{title}” dos seus textos? As viagens onde ele já foi inserido ficam com a cópia delas.',
  'snippets.edit': 'Editar texto',
  'snippets.here': 'aqui',
  'snippets.anywhere': 'Em qualquer lugar',
  'snippets.filter.destination': 'Filtrar textos por destino',
  'snippets.filter.allDestinations': 'Todos os destinos',
  'snippets.filter.category': 'Filtrar textos por tipo',
  'snippets.filter.allCategories': 'Todos os tipos',
  'snippets.field.title': 'Como chamar',
  'snippets.field.title.guidance': 'Como você vai achar de novo — “Do CDG até o hotel”, “Gorjeta na Itália”. Só você vê isso; o viajante lê o texto em si.',
  'snippets.field.category': 'Que tipo de texto',
  'snippets.field.category.guidance': 'Filtra a lista quando você já tiver muitos. Propositalmente grosseiro, e “outro” é uma resposta de verdade.',
  'snippets.field.destination': 'Onde se aplica',
  'snippets.field.destination.guidance': 'Deixe vazio para algo que vale em toda viagem — perder o passaporte, seguro, o que fazer no aeroporto. Preencha “Paris” ou “Toscana” e ele sobe para o topo da lista quando você estiver montando uma viagem para lá.',
  'snippets.field.body': 'O texto',
  'snippets.field.body.guidance': 'Escreva como o viajante deve ler, porque é exatamente isso que ele recebe. Mesma formatação de qualquer nota: **negrito**, listas, links.',
  'snippets.category.arrival': 'Chegada',
  'snippets.category.transport': 'Como circular',
  'snippets.category.practical': 'Dicas práticas',
  'snippets.category.dining': 'Comer e beber',
  'snippets.category.safety': 'Segurança',
  'snippets.category.packing': 'O que levar',
  'snippets.category.other': 'Outro',

  'trips.duplicate': 'Duplicar',
  'trips.duplicateTitle': 'Duplicar “{title}”',
  'trips.duplicateHint': 'Copia os dias, lugares, reservas e o bloco de emergência. Não copia os viajantes, os documentos deles, os links, os valores nem os números de confirmação — isso pertence à viagem que foi realmente vendida, não ao roteiro.',
  'trips.duplicate.title.guidance': 'A cópia precisa de nome próprio, senão daqui a um mês você não distingue as duas na lista.',
  'trips.duplicate.startDate.guidance': 'Quando a nova viagem começa. Todos os dias e reservas se movem junto, mantendo o horário que tinham — um museu às 09:00 continua às 09:00 mesmo que a cópia atravesse uma mudança de horário de verão. Deixe vazio para manter as datas originais.',

  'templates.title': 'Modelos',
  'templates.empty': 'Nenhum modelo ainda. Guarde como modelo uma viagem que você montaria de novo e ela aparece aqui.',
  'templates.hint': 'Uma viagem que você guarda como ponto de partida. Usar um modelo cria uma viagem nova com os mesmos dias, notas e reservas, deslocados para as datas que você indicar — e sem nada que era do cliente anterior.',
  // Não “Salvar como modelo”: nesta tela ficaria acima do “Salvar” do
  // formulário de nova viagem, e uma palavra que serve a dois botões é um
  // clique errado da Ana antes de ser um seletor ambíguo num teste.
  'templates.mark': 'Guardar como modelo',
  'templates.unmark': 'Tirar dos modelos',
  'templates.use': 'Usar modelo',
  'templates.useTitle': 'Nova viagem a partir de “{title}”',
  'templates.useHint': 'Os dias, as notas, os lugares e as reservas vêm junto, deslocados para as datas novas. Os viajantes, os documentos deles, os links, os valores e os números de confirmação não vêm — eram da viagem que foi realmente vendida.',
  'templates.create': 'Criar a viagem',
  'templates.lengthOne': '1 dia',
  'templates.length': '{count} dias',
  'templates.use.title.guidance': 'A viagem do cliente, com as palavras do cliente. O nome do modelo serve para você achá-lo nesta lista; não é o que o viajante deve ler no topo da página dele.',
  'templates.use.startDate.guidance': 'O primeiro dia da viagem nova. Os dias do modelo se encaixam a partir dele, na mesma ordem e com o mesmo intervalo, mantendo o horário de cada reserva — um museu às 09:00 continua às 09:00 mesmo que as datas novas atravessem uma mudança de horário de verão.',

  'copyDay.open': 'Copiar um dia',
  'copyDay.hint': 'Dias das suas outras viagens. Tudo o que estiver no dia é acrescentado a este — o que já está aqui permanece.',
  'copyDay.empty': 'Nada para copiar ainda. Dias das suas outras viagens aparecem aqui assim que tiverem conteúdo.',
  'copyDay.search': 'Buscar nas suas viagens',
  'copyDay.places': '{count} lugares',
  'copyDay.reservations': '{count} reservas',
  'copyDay.withNotes': 'com notas',

  // --- Ler um e-mail de confirmação ----------------------------------------
  'confirmations.title': 'Colar uma confirmação',
  'confirmations.hint': 'Cole o e-mail que o hotel ou a companhia aérea mandou e os dados voltam já separados em campos. Nada entra na viagem antes de você ler e clicar em Adicionar.',
  'confirmations.field.text': 'O e-mail',
  'confirmations.field.text.guidance': 'O e-mail inteiro, inclusive as linhas de cima. Datas que podem ser lidas de duas formas são resolvidas pelas datas desta viagem — por isso vale conferir duas vezes uma confirmação de junho colada numa viagem de julho.',
  'confirmations.placeholder': 'Cole aqui o e-mail de confirmação…',
  'confirmations.read': 'Ler',
  'confirmations.reading': 'Lendo…',
  'confirmations.unavailable': 'A leitura de confirmações não está ligada neste servidor. O resto funciona normalmente — digite a reserva à mão.',
  'confirmations.failed': 'Não deu para falar com o leitor. Tente de novo em instantes ou digite a reserva à mão.',
  'confirmations.nothingFound': 'Nada aqui parece uma reserva. Cole o e-mail inteiro, não só uma linha.',
  'confirmations.foundOne': 'Uma reserva lida.',
  'confirmations.found': '{count} reservas lidas.',
  'confirmations.checkHint': 'Confira com o e-mail antes de adicionar. O localizador principalmente — um caractere errado não aparece aqui e não funciona no balcão.',
  'confirmations.include': 'Adicionar esta',
  'confirmations.add': 'Adicionar {count} à viagem',
  'confirmations.addOne': 'Adicionar à viagem',
  'confirmations.adding': 'Adicionando…',
  'confirmations.discard': 'Descartar',
  'confirmations.when': 'Quando',
  'confirmations.ends': 'Termina',
  'confirmations.onClockIn': 'no horário de {city}',
  'confirmations.noDate': 'Sem data neste e-mail; vai para “ainda sem dia”.',
  'confirmations.outsideTrip': 'Essa data está fora desta viagem; vai para “ainda sem dia”.',
  'confirmations.noReference': 'Nenhum localizador encontrado neste e-mail.',
  'notes.title': 'Transformar minhas anotações em dias',
  'notes.hint': 'Cole suas anotações — digitadas ou copiadas do WhatsApp — e elas voltam como rascunho de itens. Nada entra na viagem antes de você ler e clicar em Adicionar.',
  'notes.field.text': 'Suas anotações',
  'notes.field.text.guidance': 'Qualquer formato serve: tópicos, conversa exportada, transcrição de áudio. “Dia 3” é contado a partir da primeira data desta viagem. O que as anotações não dizem fica em branco — endereço, horário e preço nunca são preenchidos por conta própria.',
  'notes.placeholder': 'Cole aqui suas anotações…',
  'notes.nothingFound': 'Nada aqui parece um roteiro de viagem. Cole as anotações em si, não só uma linha.',
  'notes.foundOne': 'Um item lido.',
  'notes.found': '{count} itens lidos.',
  'notes.unavailable': 'A leitura de anotações não está ligada neste servidor. O resto funciona normalmente — adicione os itens à mão.',
  'notes.failed': 'Não foi possível falar com o leitor. Tente de novo em instantes, ou adicione os itens à mão.',
  'notes.outsideTrip': 'Essa data está fora desta viagem; vai para “ainda sem dia”.',
  'notes.noDate': 'Não deu para definir uma data pelas suas anotações, então vai para “ainda sem dia”.',
  'notes.checkHint': 'Confira com suas anotações antes de adicionar. Datas e horários principalmente — são eles que colocam cada item no dia certo.',
  'confirmations.wasRead': 'Lido do e-mail',

  // --- Itens: uma coisa em um dia ------------------------------------------
  'item.add': 'Adicionar',
  'item.addFirst': 'Adicionar a primeira coisa',
  'day.editDay': 'Nome e nota deste dia',
  'item.edit': 'Editar',
  'item.editNamed': 'Editar {name}',
  'item.moreActions': 'Mais opções para {name}',
  'item.addTitle': 'Adicionar ao dia',
  'item.editTitle': 'Editar',
  'item.moreDetails': '+ mais detalhes',
  'item.fewerDetails': '− menos detalhes',
  'item.removeConfirm': 'Remover {name}? O viajante deixa de ver na hora.',
  'item.noTime': 'sem hora marcada',
  'item.field.time': 'Hora',
  'item.field.time.guidance': 'Quando acontece, se tiver hora. Deixe vazio para “o Marais, alguma tarde” — o dia se ordena sozinho, e o que não tem hora fica depois do que tem.',
  'item.field.kind': 'O que é',
  'item.field.kind.guidance': 'Define o ícone que o viajante vê e quais campos abrem primeiro. Nunca limita o que você pode preencher — um voo pode ter endereço e um restaurante pode ter localizador.',
  'item.field.endTime': 'Termina',
  'item.field.endTimezone': 'Onde termina',
  'item.field.endTimezone.guidance':
    'Só importa quando termina em outro lugar. Um voo tem dois relógios — o de onde sai e o de onde chega — e o viajante precisa da chegada no horário que o aeroporto vai dizer.',
  'item.field.title': 'Como chamar',
  'item.field.title.guidance': 'Uma linha, do jeito que o viajante deve ler: “LATAM 8064 GRU → CDG”, “Jantar no Chez Pippo”, “Transfer aeroporto → hotel”.',
  'item.field.notes': 'Notas',
  'item.field.notes.guidance': 'O que ela precisa saber sobre esta coisa — como entrar, por quem perguntar, o que já está pago. É aqui que mora o detalhe do dia agora, em vez de num bloco longo no topo.',
  'item.field.address': 'Endereço',
  'item.field.address.guidance': 'Digite o endereço e o pino do mapa vem junto — você nunca digita coordenadas. Também vira um link que abre o app de mapas do viajante.',
  'item.field.addressLocal': 'Endereço no idioma local',
  'item.field.addressLocal.guidance': 'O mesmo endereço no alfabeto local, para mostrar a um motorista que não lê português. Na página impressa, é a linha que mais resolve.',
  'item.field.phone': 'Telefone',
  'item.field.phone.guidance': 'Vira um link de ligar. Inclua o código do país, senão não completa a chamada do exterior.',
  'item.field.website': 'Site',
  'item.field.confirmationRef': 'Localizador',
  'item.field.confirmationRef.guidance': 'O número que alguém lê em voz alta num balcão. Aparece como texto puro e nunca vira link, para que um número de apólice não seja confundido com telefone.',
  'item.field.timezone': 'A hora acima é no relógio de',
  'item.field.timezone.guidance': 'Quase tudo acontece onde a viagem está, então a cidade da viagem é a resposta de sempre. O voo que cruza fusos é a exceção — diga aqui e o viajante vê a partida marcada com a cidade de onde ela sai.',
  'item.kind.flight': 'Voo',
  'item.kind.train': 'Trem',
  'item.kind.transfer': 'Transfer',
  'item.kind.car': 'Carro',
  'item.kind.hotel': 'Hotel',
  'item.kind.restaurant': 'Restaurante',
  'item.kind.cafe': 'Café',
  'item.kind.bar': 'Bar',
  'item.kind.museum': 'Museu',
  'item.kind.activity': 'Passeio',
  'item.kind.shop': 'Loja',
  'item.kind.note': 'Nota',
  'item.kind.other': 'Lugar',

  'day.field.title': 'Como chamar este dia',
  'day.field.title.guidance': 'Como o dia aparece na lista do viajante: “Versalhes”, “Chegada em Paris”, “Feliz Aniversário, Maria!”. Deixe em branco e ele vê a data.',
  'day.untitled': 'Sem nome ainda',
  'day.notesShort': 'Uma linha sobre o dia',
  'day.notesShort.guidance': 'Uma frase acima da lista do dia — “Dia livre no Marais”. O detalhe fica nas próprias coisas agora, que é onde o viajante procura.',
  'day.empty': 'Nada neste dia ainda.',
  'day.itemCountOne': '1 coisa',
  'day.itemCount': '{count} coisas',

  'map.title': 'Onde fica tudo',
  'map.unavailable': 'Não foi possível carregar o mapa.',
  'map.openIn': 'Abrir no Maps',

  'error.validation_failed': 'Confira os campos destacados.',
  'error.unauthorized': 'Faça login.',
  'error.forbidden': 'Você não tem acesso a isso.',
  'error.not_found': 'Não encontrado.',
  'error.conflict': 'Isso conflita com algo que já existe.',
  'error.rate_limited': 'Tentativas demais. Aguarde um instante.',
  'error.consent_required': 'Essa informação exige um consentimento registrado antes de poder ser salva.',
  'error.internal': 'Algo deu errado do nosso lado.',
  'nav.intake': 'Pedidos de viagem',
  'intake.inbox.title': 'Pedidos de viagem',
  'intake.inbox.hint': 'O que cada cliente respondeu no formulário. Nada aqui chega ao link do viajante.',
  'intake.inbox.empty': 'Nenhum pedido ainda. Envie o link do formulário depois da primeira conversa no WhatsApp.',
  'intake.inbox.linkLabel': 'Link do formulário para enviar ao cliente',
  'intake.inbox.copy': 'Copiar link',
  'intake.inbox.copied': 'Link copiado',
  'intake.inbox.open': 'Ver como o cliente vê',
  'intake.inbox.new': 'Novo',
  'intake.inbox.markHandled': 'Marcar como atendido',
  'intake.inbox.reopen': 'Reabrir',
  'intake.inbox.whatsapp': 'Abrir no WhatsApp',
  'intake.inbox.travellers': '{adults} adulto(s), {children} criança(s)',
  'intake.inbox.lastTrip': 'Última viagem parecida',
  'intake.inbox.notes': 'Observações',
  'intake.kicker': 'Longhi Travel',
  'intake.title': 'Vamos desenhar a sua viagem',
  'intake.lead': 'Leva uns cinco minutos. Não existe resposta errada: se não souber algo, diga que não sabe. A Ana usa o que você contar para preparar uma conversa de verdade, não um orçamento genérico.',
  'intake.step.who': 'Quem viaja',
  'intake.step.trip': 'A viagem',
  'intake.step.style': 'O estilo da viagem',
  'intake.step.invest': 'Seu investimento',
  'intake.step.finish': 'Para finalizar',
  'intake.next': 'Continuar',
  'intake.back': 'Voltar',
  'intake.name': 'Seu nome',
  'intake.whatsapp': 'WhatsApp',
  'intake.whatsapp.hint': 'Com DDD. É por aqui que a Ana vai falar com você.',
  'intake.email': 'E-mail (opcional)',
  'intake.destination': 'Para onde vocês sonham em ir?',
  'intake.destination.hint': 'Um país, uma cidade ou só uma ideia, como “algo na Europa com muita comida boa”.',
  'intake.when': 'Quando?',
  'intake.when.hint': 'Do jeito que você falaria: “julho de 2027”, “nas férias das crianças”.',
  'intake.duration': 'Quantos dias, mais ou menos?',
  'intake.adults': 'Adultos',
  'intake.children': 'Crianças',
  'intake.childrenAges': 'Idades das crianças',
  'intake.childrenAges.hint': 'Ex.: 4 e 9',
  'intake.comfort.legend': 'Como você imagina o conforto da viagem?',
  'intake.comfort.hint': 'Escolha o que mais combina com vocês. Dá para ajustar depois.',
  'intake.comfort.essential.title': 'Essencial',
  'intake.comfort.essential.desc': 'Bom custo-benefício. Hotéis simples e bem localizados, restaurantes locais, foco em viver o destino.',
  'intake.comfort.comfortable.title': 'Confortável',
  'intake.comfort.comfortable.desc': 'Hotéis 4 estrelas ou boutique, mistura de restaurantes casuais e algumas mesas especiais, passeios guiados quando valem a pena.',
  'intake.comfort.premium.title': 'Premium',
  'intake.comfort.premium.desc': 'Hotéis 5 estrelas ou charmosos de design, traslados privativos, experiências que não se acham sozinho.',
  'intake.comfort.luxury.title': 'Sem concessões',
  'intake.comfort.luxury.desc': 'Os melhores hotéis e suítes, acesso exclusivo, serviço sob medida de ponta a ponta.',
  'intake.invest.lead': 'Ninguém precisa saber de cabeça quanto custa uma viagem. Por isso não pedimos um orçamento: a Ana monta a proposta depois de pesquisar e conversa com você sobre valores. Estas três perguntas só ajudam a acertar o ponto de partida.',
  'intake.lastTrip': 'Qual foi a última viagem parecida que vocês fizeram?',
  'intake.lastTrip.hint': 'Destino, quantos dias e, se lembrar, mais ou menos quanto gastaram. Pode ser aproximado.',
  'intake.band.legend': 'Por pessoa, sem contar a passagem aérea, o que parece confortável?',
  'intake.band.upto15': 'Até R$ 15 mil',
  'intake.band.from15to30': 'R$ 15 a 30 mil',
  'intake.band.from30to60': 'R$ 30 a 60 mil',
  'intake.band.above60': 'Acima de R$ 60 mil',
  'intake.band.unsure': 'Ainda não sei, prefiro que a Ana me oriente',
  'intake.interests.legend': 'O que não pode faltar?',
  'intake.interest.gastronomy': 'Gastronomia',
  'intake.interest.culture': 'Cultura e história',
  'intake.interest.nature': 'Natureza',
  'intake.interest.beach': 'Praia e descanso',
  'intake.interest.shopping': 'Compras',
  'intake.interest.adventure': 'Aventura',
  'intake.interest.wellness': 'Bem-estar',
  'intake.interest.family': 'Momentos em família',
  'intake.interest.celebration': 'Celebração especial',
  'intake.notes': 'Algo mais que a Ana deva saber?',
  'intake.notes.hint': 'Ritmo, lugares que já conhecem, desejos. Não escreva aqui dados de saúde nem de documentos; isso a Ana pede depois, com cuidado.',
  'intake.source.legend': 'Como você chegou até a Ana?',
  'intake.source.instagram': 'Instagram',
  'intake.source.ads': 'Anúncio',
  'intake.source.referral': 'Indicação',
  'intake.source.whatsapp': 'Conversa no WhatsApp',
  'intake.source.other': 'Outro',
  'intake.consent': 'Concordo que a Longhi Travel use estas respostas para entrar em contato comigo e preparar a minha viagem. Posso pedir a exclusão a qualquer momento.',
  'intake.submit': 'Enviar para a Ana',
  'intake.sending': 'Enviando…',
  'intake.done.title': 'Recebemos, obrigada!',
  'intake.done.body': 'A Ana vai ler tudo com calma e responder no seu WhatsApp. Se quiser adiantar, escolha um horário para uma conversa rápida.',
  'intake.done.bookTitle': 'Escolha um horário para conversar',
  'intake.done.noBooking': 'Em breve a Ana entra em contato pelo WhatsApp.',
  'intake.notFound': 'Este link não está disponível. Peça à Ana para reenviar.',
}
